import { FilterTag, styled } from "@volkswagen-onehub/components-core";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import useFiltersQuery from "../api/use-filters-query";
import { useAppliedFilterStore } from "../store/filtersv2";
import { getLang, getTranslations } from "../translations";
import { BasicFilterOption, ColourFilterOption, FiltersResponse, FiltersResponseKey, ModelFilterOption } from "../types";
import { Filters } from "../types/filters";
import { formatCurrency, formatDistance } from "../utils/format";
import { useFiltering } from "../utils/use-filtering";

interface ClearableFilter {
  text: string,
  keyName: keyof Filters,
  onClick: () => void;
  className?: string;
  hexCode?: string;
}

const StyledFilterTagWrapper = styled.div<{hexCode?: string}>`
  ${
    ({className, hexCode}) => hexCode
    ? `
    &.${className} {
      span {
      display: flex;
      align-items: center;
        ::after {
          content: "";
          margin-left: 4px;
          width: 16px;
          height: 16px;
          display: block;
          background-color: ${hexCode};
          border-radius: 50%;
        }
      }
    }
    `
    : null
  }
`;

export const SelectedFilters = () => {
  const lang = getLang();
  const { labels } = getTranslations();
  const location = useLocation();
  const isOnSRP = location.pathname.includes("/results");

  const { isLoading, data: filterOptions } = useFiltersQuery<FiltersResponse, FiltersResponse>();
  const { applied } = useAppliedFilterStore();
  const { applyQuickFilter } = useFiltering();

  const getSelectedFilters = <T,>(applied: Filters) => {
    const clearableFilters: ClearableFilter[] = [];
    for (const key in applied) {
      const value = applied[key as keyof Filters] as T;
      if (value) {
        switch (key) {
          case "year":
            const singleYear = (value as number[])[0] === (value as number[])[1];
            clearableFilters.push({
              keyName: key,
              text: singleYear ? String((value as number[])[0]) : `${(value as number[])[0]}-${(value as number[])[1]}`,
              onClick: () => applyQuickFilter(key, undefined)
            });
            break;
          case "price":
            clearableFilters.push({
              keyName: key,
              text: `${labels.up_to} ${formatCurrency(value as number, false, lang)}`,
              onClick: () => applyQuickFilter(key, undefined)
            });
            break;
          case "mileage":
            clearableFilters.push({
              keyName: key,
              text: `${labels.up_to} ${formatDistance(value as number, lang)}`, // NTS translation needed
              onClick: () => applyQuickFilter(key, undefined)
            });
            break;
          case "color_exterior":
          case "color_interior":
            const colourFilterConfig = filterOptions?.[key as FiltersResponseKey] as ColourFilterOption[];
            const colourSelection = value as string[];
            colourSelection.forEach(x => {
              clearableFilters.push({
                keyName: key,
                text: key === "color_exterior" ? "Ext." : "Int.",
                onClick: () => applyQuickFilter(key, applied[key]?.filter(z => z !== x)),
                hexCode: colourFilterConfig?.filter(y => y?.value === x)[0]?.hex_code ?? "",
                className: `${key}-${x}`
              })
            });
            break;
          case "model":
            const modelFilterConfig = filterOptions?.[key as FiltersResponseKey] as ModelFilterOption[];
            const modelSelection = value as string[];
            modelSelection.forEach(x => {
              clearableFilters.push({
                keyName: key,
                text: modelFilterConfig?.filter(y => y.value === x)[0].label[lang],
                onClick: () => applyQuickFilter(key, applied[key]?.filter(z => z !== x))
              })
            });
            break;
          case "category":
          case "trimline":
          case "transmission":
          case "color_exterior":
          case "color_interior":
            const filterConfig = filterOptions?.[key as FiltersResponseKey] as BasicFilterOption[];
            const selection = value as string[];
            selection.forEach(x => {
              if (x !== "new" && x !== "used" && x !== "demo" && !(!isOnSRP && x === "manual")) {
                clearableFilters.push({
                  keyName: key,
                  text: filterConfig?.filter(y => y.value === x)[0].label[lang],
                  onClick: () => applyQuickFilter(key, applied[key]?.filter(z => z !== x))
                })
              }
            });
            break;
          default:
            break;
        }
      }
    }
    return clearableFilters;
  };

  useEffect(() => {
    getSelectedFilters(applied);
  }, [applied]);

  return (
    <>
      {
        isLoading
        ? <></>
        : <>
          { getSelectedFilters(applied).map(({keyName, text, onClick, className, hexCode}, i) => {
            return (
              <StyledFilterTagWrapper
                key={`${keyName}-${i}`}
                className={className} hexCode={hexCode}>
                <FilterTag
                  emphasis="secondary"
                  // iconPosition="end"
                  active={true}
                  ariaPressed={true}
                  text={text}
                  onClick={onClick}
                  // ariaLabel={`${text} ${labels.click_clear}`}
                />
              </StyledFilterTagWrapper>
            )
          }) }
        </>
      }
    </>
  );
};
